import React, { useMemo, useState, useEffect } from 'react';
import CalendarHeatmap from 'react-calendar-heatmap';
import 'react-calendar-heatmap/dist/styles.css';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { subDays, format, eachDayOfInterval, parseISO, startOfYear, endOfYear, addDays } from 'date-fns';
import { Document } from '../types';
import { Select, Typography, Row, Col, Spin, message } from 'antd';
import noteService from '../services/noteService';
import authService from '../services/authService';

const { Title, Text } = Typography;

interface ActivityHeatmapProps {
    documents: Document[];
    onDateSelect?: (date: Date) => void;
}

const WEEKDAYS = ['', '一', '', '三', '', '五', ''];
const MONTHS = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'];

const ActivityHeatmap: React.FC<ActivityHeatmapProps> = ({ documents, onDateSelect }) => {
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedDateBox, setSelectedDateBox] = useState<string | null>(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    useEffect(() => {
        setIsAuthenticated(authService.isAuthenticated());
    }, []);

    // 生成年份选项
    const yearOptions = useMemo(() => {
        const currentYear = new Date().getFullYear();
        const startYear = 2020; // 或者从用户注册年份开始
        const years = [];
        for (let year = currentYear; year >= startYear; year--) {
            years.push({ label: `${year}年`, value: year });
        }
        return years;
    }, []);

    // 计算热力图数据
    const { activityData, startDate, endDate } = useMemo(() => {
        const start = startOfYear(new Date(selectedYear, 0, 1));
        const end = endOfYear(new Date(selectedYear, 11, 31));

        // 生成一年中的所有日期
        const allDates = eachDayOfInterval({ start, end });
        const dateMap = new Map<string, number>();

        // 初始化所有日期为0
        allDates.forEach(date => {
            dateMap.set(format(date, 'yyyy-MM-dd'), 0);
        });

        // 使用传入的documents数据
        const docsToProcess = documents;

        // 统计每天的文档活动数量（创建+更新）
        if (docsToProcess && docsToProcess.length > 0) {
            docsToProcess.forEach(doc => {
                try {
                    const createdAt = new Date(doc.createdAt);
                    const updatedAt = new Date(doc.updatedAt || doc.createdAt);

                    // 检查是否是有效日期
                    if (isNaN(createdAt.getTime()) || isNaN(updatedAt.getTime())) {
                        return;
                    }

                    // 统计创建日期
                    const createdDateStr = format(createdAt, 'yyyy-MM-dd');
                    if (selectedYear === createdAt.getFullYear()) {
                        if (dateMap.has(createdDateStr)) {
                            dateMap.set(createdDateStr, (dateMap.get(createdDateStr) || 0) + 1);
                        } else {
                            dateMap.set(createdDateStr, 1);
                        }
                    }

                    // 如果更新时间与创建时间不同，也统计更新日期
                    const updatedDateStr = format(updatedAt, 'yyyy-MM-dd');
                    if (createdDateStr !== updatedDateStr && selectedYear === updatedAt.getFullYear()) {
                        if (dateMap.has(updatedDateStr)) {
                            dateMap.set(updatedDateStr, (dateMap.get(updatedDateStr) || 0) + 1);
                        } else {
                            dateMap.set(updatedDateStr, 1);
                        }
                    }
                } catch (e) {
                    // 静默处理文档日期错误
                }
            });
        }

        // 转换为热力图需要的格式
        const data = Array.from(dateMap.entries()).map(([dateStr, count]) => ({
            date: dateStr,
            count
        }));



        return {
            activityData: data,
            startDate: start,
            endDate: end
        };
    }, [documents, selectedYear, isAuthenticated]);

    const getClassForValue = (value: any) => {
        if (!value || !value.date) return 'color-empty';

        // 添加选中状态的样式
        const dateStr = value.date ? format(value.date, 'yyyy-MM-dd') : '';
        const isSelected = dateStr === selectedDateBox;

        if (!value.count) {
            return isSelected ? 'color-empty-selected' : 'color-empty';
        }

        // 根据计数确定基本颜色类
        let colorClass = '';
        if (value.count === 1) colorClass = 'color-github-1';
        else if (value.count === 2) colorClass = 'color-github-2';
        else if (value.count === 3) colorClass = 'color-github-3';
        else colorClass = 'color-github-4';

        // 如果被选中，添加选中状态类
        return isSelected ? `${colorClass} color-github-selected` : colorClass;
    };

    const getTooltipTitle = (value: any) => {
        if (!value || !value.date) return '';
        const date = new Date(value.date);
        const count = value.count || 0;
        const dateStr = format(date, 'yyyy年MM月dd日');
        return `${dateStr}: ${count}次${isAuthenticated ? '笔记' : '文档'}活动`;
    };

    const handleDateClick = (value: any) => {
        if (!value || !value.date) return;

        const clickedDate = new Date(value.date);
        const dateStr = format(clickedDate, 'yyyy-MM-dd');

        // 设置选中的日期框
        setSelectedDateBox(dateStr);

        // 调用父组件的回调函数
        if (onDateSelect) {
            onDateSelect(clickedDate);
        }
    };

    return (
        <div className="activity-heatmap-card">
            <div className="heatmap-header">
                <Title level={4} style={{ margin: 0 }}>频率热力图</Title>
                <div className="year-selector">
                    <Text style={{ marginRight: 6 }}>选择年份:</Text>
                    <Select
                        value={selectedYear}
                        onChange={(year) => {
                            setSelectedYear(year);
                            setSelectedDateBox(null); // 清除选中的日期
                        }}
                        options={yearOptions}
                        style={{ width: 100 }}
                        size="small"
                    />
                </div>
            </div>

            <div className="heatmap-container">
                <CalendarHeatmap
                    startDate={startDate}
                    endDate={endDate}
                    values={activityData}
                    classForValue={getClassForValue}
                    tooltipDataAttrs={(value) => ({
                        'data-tooltip-id': 'heatmap-tooltip',
                        'data-tooltip-content': getTooltipTitle(value),
                    })}
                    showWeekdayLabels={true}
                    weekdayLabels={WEEKDAYS}
                    monthLabels={MONTHS}
                    horizontal={true}
                    gutterSize={3}
                    showMonthLabels={true}
                    monthLabelHeight={24}
                    onClick={handleDateClick}
                />
                <ReactTooltip id="heatmap-tooltip" />
            </div>

            {/* 图例 */}
            <div className="heatmap-legend">
                <Text type="secondary" className="legend-label">较少</Text>
                <div className="legend-squares">
                    <div className="legend-square color-empty"></div>
                    <div className="legend-square color-github-1"></div>
                    <div className="legend-square color-github-2"></div>
                    <div className="legend-square color-github-3"></div>
                    <div className="legend-square color-github-4"></div>
                </div>
                <Text type="secondary" className="legend-label">较多</Text>
            </div>
        </div>
    );
};

export default ActivityHeatmap;
